import {isDraftInNewConversation} from '../../../../helpers/models/messageHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';

/*
 * Types.
 */

export enum ComposerHeaderBannerTypesEnum {
  RECENT_COMMUNICATIONS = 'RECENT_COMMUNICATIONS',
}

export enum ComposerHeaderBannerActionsEnum {
  DISMISS_BANNER = 'DISMISS_BANNER',
  SET_AS_CAN_BE_DISPLAYED = 'SET_AS_CAN_BE_DISPLAYED',
  SET_AS_NO_BANNER = 'SET_AS_NO_BANNER',
}

export enum ComposerHeaderBannerDisplayStatusesEnum {
  CAN_BE_DISPLAYED = 'CAN_BE_DISPLAYED',
  DISMISSED = 'DISMISSED',
  NO_BANNER = 'NO_BANNER',
  UNSET = 'UNSET',
}

interface BaseBannerParameters {
  displayStatus: ComposerHeaderBannerDisplayStatusesEnum;
}

type ComposerHeaderBannerState = {
  [ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS]: BaseBannerParameters;
};

/*
 * Actions.
 */

interface DismissBannerAction {
  type: ComposerHeaderBannerActionsEnum.DISMISS_BANNER;
  payload: {
    type: ComposerHeaderBannerTypesEnum;
  };
}

interface SetAsCanBeDisplayedAction {
  type: ComposerHeaderBannerActionsEnum.SET_AS_CAN_BE_DISPLAYED;
  payload: {
    type: ComposerHeaderBannerTypesEnum;
  };
}

interface SetAsNoBannerAction {
  type: ComposerHeaderBannerActionsEnum.SET_AS_NO_BANNER;
  payload: {
    type: ComposerHeaderBannerTypesEnum;
  };
}

type ComposerHeaderBannerAction = DismissBannerAction | SetAsCanBeDisplayedAction | SetAsNoBannerAction;

/*
 * Actions Creators.
 */

export function dismissBanner(type: ComposerHeaderBannerTypesEnum): DismissBannerAction {
  return {
    type: ComposerHeaderBannerActionsEnum.DISMISS_BANNER,
    payload: {type},
  };
}

export function setAsCanBeDisplayed(type: ComposerHeaderBannerTypesEnum): SetAsCanBeDisplayedAction {
  return {
    type: ComposerHeaderBannerActionsEnum.SET_AS_CAN_BE_DISPLAYED,
    payload: {type},
  };
}

export function setAsNoBanner(type: ComposerHeaderBannerTypesEnum): SetAsNoBannerAction {
  return {
    type: ComposerHeaderBannerActionsEnum.SET_AS_NO_BANNER,
    payload: {type},
  };
}

/*
 * Reducer.
 */

export const initialComposerHeaderBannerState: ComposerHeaderBannerState = {
  [ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS]: {
    displayStatus: ComposerHeaderBannerDisplayStatusesEnum.UNSET,
  },
};

export function composerHeaderBannerReducer(
  state: ComposerHeaderBannerState = initialComposerHeaderBannerState,
  action: ComposerHeaderBannerAction,
) {
  switch (action.type) {
    case ComposerHeaderBannerActionsEnum.DISMISS_BANNER:
      return {
        ...state,
        [action.payload.type]: {displayStatus: ComposerHeaderBannerDisplayStatusesEnum.DISMISSED},
      };
    case ComposerHeaderBannerActionsEnum.SET_AS_CAN_BE_DISPLAYED:
      return {
        ...state,
        [action.payload.type]: {displayStatus: ComposerHeaderBannerDisplayStatusesEnum.CAN_BE_DISPLAYED},
      };

    case ComposerHeaderBannerActionsEnum.SET_AS_NO_BANNER:
      return {
        ...state,
        [action.payload.type]: {displayStatus: ComposerHeaderBannerDisplayStatusesEnum.NO_BANNER},
      };
    default:
      return state;
  }
}

/*
 * Render Helpers.
 */

export function computeCanRenderRecentCommunicationsBanner(
  conversation: ConversationModel | null,
  message: DraftMessageModel,
  hasRecentCommunicationsComposerBanner: boolean,
) {
  if (!conversation || !hasRecentCommunicationsComposerBanner) {
    return false;
  }

  // We don't want to show the banner if there's no recipient or if the main recipient is BCC'd
  const conversationRecipient = conversation.recipient;
  if (!conversationRecipient || conversationRecipient.role === RecipientRolesEnum.BCC) {
    return false;
  }

  // Don't show the banner when it's a new conversation, e.g. when composing a new message
  if (isDraftInNewConversation(message)) {
    return false;
  }

  return true;
}
